import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { usePlayerStore } from '@/store/player-store';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, User, Trophy, TrendingUp, Calendar, Hash } from 'lucide-react';
import { getPlayerRecords } from '@/db';
import type { GameRecord } from '@/db';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface ProfilePageProps {
  onNavigate: (page: 'home') => void;
}

export default function ProfilePage({ onNavigate }: ProfilePageProps) {
  const { currentProfile } = usePlayerStore();
  const [records, setRecords] = useState<GameRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (currentProfile) {
      loadRecords();
    }
  }, [currentProfile]);

  const loadRecords = async () => {
    if (!currentProfile) return;
    setIsLoading(true);
    const data = await getPlayerRecords(currentProfile.id);
    setRecords(data.slice(0, 20)); // 最近20条
    setIsLoading(false);
  };

  // 准备图表数据
  const chartData = records
    .slice()
    .reverse()
    .map((record, index) => ({
      hand: index + 1,
      chips: record.chipChange,
      score: record.scoreChange,
    }));

  if (!currentProfile) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-white text-lg mb-4">请先选择玩家档案</p>
          <Button onClick={() => onNavigate('home')}>返回首页</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* 顶部导航 */}
      <nav className="p-4 flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onNavigate('home')}
          className="text-white hover:bg-slate-800"
        >
          <ArrowLeft className="w-6 h-6" />
        </Button>
        <h1 className="text-xl font-bold text-white">个人中心</h1>
      </nav>

      {/* 主内容 */}
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* 玩家信息卡片 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-6">
                <div className="flex items-center gap-6">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center">
                    <User className="w-10 h-10 text-white" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-white">{currentProfile.name}</h2>
                    <p className="text-slate-400">ID: {currentProfile.id}</p>
                    <div className="flex gap-6 mt-3">
                      <div>
                        <p className="text-slate-500 text-sm">筹码</p>
                        <p className="text-emerald-400 font-bold text-xl">{currentProfile.chips.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-slate-500 text-sm">积分</p>
                        <p className="text-amber-400 font-bold text-xl">{currentProfile.score.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-slate-500 text-sm">注册时间</p>
                        <p className="text-white">{new Date(currentProfile.createdAt).toLocaleDateString()}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* 统计数据 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4"
          >
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-slate-400 mb-2">
                  <Hash className="w-4 h-4" />
                  <span className="text-sm">总场次</span>
                </div>
                <p className="text-2xl font-bold text-white">{currentProfile.stats.totalGames}</p>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-slate-400 mb-2">
                  <Trophy className="w-4 h-4" />
                  <span className="text-sm">胜率</span>
                </div>
                <p className="text-2xl font-bold text-white">{currentProfile.stats.winRate.toFixed(1)}%</p>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-slate-400 mb-2">
                  <TrendingUp className="w-4 h-4" />
                  <span className="text-sm">连胜</span>
                </div>
                <p className="text-2xl font-bold text-white">{currentProfile.stats.currentStreak}</p>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-slate-400 mb-2">
                  <Calendar className="w-4 h-4" />
                  <span className="text-sm">最大赢取</span>
                </div>
                <p className="text-2xl font-bold text-white">{currentProfile.stats.biggestPot.toLocaleString()}</p>
              </CardContent>
            </Card>
          </motion.div>

          {/* 图表 */}
          {chartData.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white">近期战绩</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="hand" stroke="#94a3b8" />
                        <YAxis stroke="#94a3b8" />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#1e293b', 
                            border: '1px solid #334155',
                            borderRadius: '8px'
                          }}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="chips" 
                          stroke="#10b981" 
                          strokeWidth={2}
                          dot={false}
                          name="筹码变化"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* 最近记录 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">最近游戏记录</CardTitle>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <p className="text-slate-400 text-center py-4">加载中...</p>
                ) : records.length === 0 ? (
                  <p className="text-slate-400 text-center py-4">暂无游戏记录</p>
                ) : (
                  <div className="space-y-2">
                    {records.slice(0, 10).map((record) => (
                      <div 
                        key={record.id}
                        className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg"
                      >
                        <div>
                          <p className="text-white font-medium">
                            {record.mode === 'casual' ? '休闲场' :
                             record.mode === 'tournament_low' ? '锦标赛（低）' :
                             record.mode === 'tournament_mid' ? '锦标赛（中）' :
                             record.mode === 'tournament_high' ? '锦标赛（高）' : '急速赛'}
                          </p>
                          <p className="text-slate-500 text-sm">
                            {new Date(record.timestamp).toLocaleString()}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className={`font-bold ${record.chipChange >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {record.chipChange >= 0 ? '+' : ''}{record.chipChange.toLocaleString()}
                          </p>
                          <p className="text-slate-500 text-sm">第 {record.placement} 名</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
